import { EphemeralExercise } from "../../../models/EphemeralExercise";
import ApiService from "../../../services/ApiService";
import translate from "../../../services/Translate";
import {
  CursusOption,
  EphemeralCreationForm,
  EphemeralCreationPayload,
  TopicRatioFormItem,
  TopicRatioPayloadItem,
} from "./CreationModal.d";

export const QCM_LANGUAGES = [
  { value: "fr", labelKey: "FRENCH" },
  { value: "en", labelKey: "ENGLISH" },
] as const;

export const getInitialForm = (): EphemeralCreationForm => ({
  name: "",
  title: "",
  duration: 60,
  mandatory_ratio: 0.5,
  topics_ratio: [{ name: "", percentage: "" }],
  language: "en",
  notes: "",
  pool_length: "",
  randomize: false,
  send_result: false,
  is_deprecated: false,
});

export const parseTopicsRatio = (topics: TopicRatioFormItem[]): TopicRatioPayloadItem[] =>
  topics
    .filter((topic) => topic.name.trim() && topic.percentage !== "")
    .map((topic) => ({
      name: topic.name.trim(),
      percentage: Number(topic.percentage),
    }));

export const buildPayload = (
  form: EphemeralCreationForm,
  selectedExos: EphemeralExercise[],
  selectedCursus: CursusOption | null
): EphemeralCreationPayload => {
  const payload: EphemeralCreationPayload = {
    name: form.name.trim(),
    title: form.title.trim(),
    duration: Number(form.duration) * 60,
    exos: selectedExos.map((exo) => exo.ID),
    mandatory_ratio: form.mandatory_ratio,
    topics_ratio: parseTopicsRatio(form.topics_ratio),
    randomize: form.randomize,
    send_result: form.send_result,
    is_deprecated: form.is_deprecated,
  };

  if (selectedCursus?.id != null) {
    payload.cursus = String(selectedCursus.id);
  }
  if (form.language) {
    payload.language = form.language;
  }
  if (form.notes.trim()) {
    payload.notes = form.notes.trim();
  }
  if (form.pool_length !== "") {
    payload.pool_length = Number(form.pool_length);
  }

  return payload;
};

export const isFormInvalid = (form: EphemeralCreationForm, selectedExos: EphemeralExercise[]): boolean => {
  const topics = parseTopicsRatio(form.topics_ratio);
  const hasIncompleteTopic = form.topics_ratio.some(
    (topic) => (topic.name.trim() && topic.percentage === "") || (!topic.name.trim() && topic.percentage !== "")
  );

  return (
    !form.name.trim() ||
    !form.title.trim() ||
    Number(form.duration) < 1 ||
    !selectedExos.length ||
    Number.isNaN(form.mandatory_ratio) ||
    form.mandatory_ratio < 0 ||
    form.mandatory_ratio > 1 ||
    !topics.length ||
    hasIncompleteTopic ||
    topics.some((topic) => Number.isNaN(topic.percentage) || topic.percentage < 0 || topic.percentage > 100) ||
    (form.pool_length !== "" && (!Number.isInteger(Number(form.pool_length)) || Number(form.pool_length) < 0))
  );
};

export const fetchCursus = (language: string, setCursus: Function, setLoading: Function) => {
  setLoading(true);
  ApiService.get("cursus?order_by=name&order=asc")
    .then((response) => setCursus(response.data.cursus))
    .catch((error) => {
      console.error("Error loading cursus:", error);
      alert(translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.ERRORS.FETCH_CURSUS"));
    })
    .finally(() => setLoading(false));
};

export const fetchEphemeralExercises = (language: string, setExercises: Function, setLoading: Function) => {
  setLoading(true);
  ApiService.get("ephemeral-evaluations/exercises?only_active=true&order_by=resume&order=asc")
    .then((response) => setExercises(response.data.ephemeral_exercises))
    .catch((error) => {
      console.error("Error loading ephemeral exercises:", error);
      alert(translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.ERRORS.FETCH_EXERCISES"));
    })
    .finally(() => setLoading(false));
};

export const createEphemeralEvaluation = (
  payload: EphemeralCreationPayload,
  language: string,
  setLoading: Function,
  displayAlert: Function,
  setShowCreationModal: Function,
  setShowAlert: Function,
  resetSelection: Function,
  onCreated?: Function
) => {
  setLoading(true);
  ApiService.post("ephemeral-evaluations", payload)
    .then(() => {
      setShowCreationModal(false);
      onCreated?.();
      displayAlert({
        type: "success",
        title: translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.DST_ALERT.SUCCESS.TITLE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.DST_ALERT.SUCCESS.CREATE_ANOTHER"),
            variant: "primary",
            onClick: () => {
              setShowAlert(false);
              setShowCreationModal(true);
            },
          },
        ],
      });
      resetSelection();
    })
    .catch((error) => {
      console.error("Error creating ephemeral evaluation:", error);
      displayAlert({
        type: "danger",
        title: translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.DST_ALERT.ERROR.TITLE"),
        buttons: [
          {
            label: translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.DST_ALERT.ERROR.TRY_AGAIN"),
            variant: "primary",
            onClick: () => setShowAlert(false),
          },
        ],
      });
    })
    .finally(() => setLoading(false));
};
